// react_frontend/src/Components/Partials/Sidebar.js
import React, { useRef, useEffect, useContext, useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MotherContext } from "../../Context/MotherContext";
import { SupportContext } from "../../Context/SupportContext";
import { permissions } from "../Router/permissions";
import SupportWidget from "../Modules/SupportWidget";
import defaultLogo from "../../Assets/Media/Logo.png";

import {
  FaUsers, FaClock, FaCalendarAlt, FaMoneyBillWave, FaFileAlt,
  FaBoxOpen, FaReceipt, FaFileInvoice, FaUserPlus, FaChartLine,
  FaRobot, FaCheckCircle, FaInfoCircle, FaAngleDown, FaTachometerAlt,
  FaColumns, FaBars, FaWarehouse, FaCreditCard, FaChartArea, FaHandshake,
  FaUserFriends, FaCog, FaList, FaHeadset, FaUser, FaWallet, FaBook, FaBriefcase, FaFolderOpen,
  FaBalanceScale, FaUniversity, FaHourglassHalf, FaUndo, FaBarcode, FaGlobe, FaCogs, FaIndustry, FaExclamationTriangle,
} from "react-icons/fa";
import { AiOutlineProduct } from "react-icons/ai";

const Sidebar = () => {
  const { company } = useContext(MotherContext);
  const { showSupport, setShowSupport } = useContext(SupportContext);
  const location = useLocation();
  const role = parseInt(localStorage.getItem("who_am_i"), 10);
  const can = permissions[role] || {};

  const [openMenus, setOpenMenus] = useState({});
  const supportWidgetRef = useRef(null);

  const getLogoUrl = useCallback(() => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
    if (company?.logo) {
      const cleanPath = company.logo.replace(/\\/g, "/").split(" ").join("%20");
      return `${apiBaseUrl}/${cleanPath}`;
    }
    return defaultLogo;
  }, [company?.logo]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showSupport) setShowSupport(false);
    };
    if (showSupport) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showSupport, setShowSupport]);

  useEffect(() => {
    const handleClose = () => setShowSupport(false);
    window.addEventListener("closeSupportChat", handleClose);
    return () => window.removeEventListener("closeSupportChat", handleClose);
  }, [setShowSupport]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        window.innerWidth <= 992 &&
        document.body.classList.contains("sb-mobile-open") &&
        !e.target.closest(".modern-sidebar") &&
        !e.target.closest(".nav-icon-btn")
      ) {
        document.body.classList.remove("sb-mobile-open");
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    setOpenMenus({});
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.add("app-layout");
    return () => document.body.classList.remove("app-layout");
  }, []);

  const closeMobile = () => document.body.classList.remove("sb-mobile-open");
  const isActive = (path) => location.pathname === path;
  const isParentActive = (paths = []) => paths.some((p) => location.pathname.startsWith(p));

  const isMenuOpen = (key, paths) =>
    openMenus[key] !== undefined ? openMenus[key] : isParentActive(paths);

  const handleParentClick = (key, paths) => {
    if (document.body.classList.contains("sb-mini")) {
      document.body.classList.remove("sb-mini");
      setOpenMenus((prev) => ({ ...prev, [key]: true }));
      return;
    }
    setOpenMenus((prev) => ({ ...prev, [key]: !isMenuOpen(key, paths) }));
  };

  const apiUrl = process.env.REACT_APP_API_BASE_URL + "/api";

  const renderMenuItem = (to, Icon, label) => (
    <Link
      key={to}
      to={to}
      title={label}
      className={`sidebar-link ${isActive(to) ? "active" : ""}`}
      onClick={closeMobile}
    >
      {Icon && <Icon className="link-icon" />}
      <span>{label}</span>
    </Link>
  );

  const renderMenuSection = ({ title, menuKey, icon: Icon, paths = [], children }) => {
    const isOpen = isMenuOpen(menuKey, paths);
    const active = isParentActive(paths);
    return (
      <div className="sidebar-menu-section" key={menuKey}>
        <button
          type="button"
          title={title}
          className={`menu-parent ${active ? "active" : ""} ${isOpen ? "open" : ""}`}
          onClick={() => handleParentClick(menuKey, paths)}
        >
          <div className="menu-icon">{Icon && <Icon />}</div>
          <span className="menu-label">{title}</span>
          <FaAngleDown className={`menu-arrow ${isOpen ? "rotated" : ""}`} />
        </button>
        <div className={`menu-children ${isOpen ? "open" : ""}`}>{children}</div>
      </div>
    );
  };

  return (
    <div className="modern-sidebar">
      <div className="sidebar-scroll">
        <div className="sidebar-nav">
          <Link
            to={role === 4 ? "/hr/employee-dashboard" : "/dashboard"}
            title="Dashboard"
            className={`sidebar-link main ${isActive(role === 4 ? "/hr/employee-dashboard" : "/dashboard") ? "active" : ""}`}
            onClick={closeMobile}
          >
            <FaTachometerAlt className="link-icon" />
            <span>Dashboard</span>
          </Link>

          {/* ===== HR ===== */}
          {can.hr?.length > 0 && (
            <>
              <div className="section-divider"><span className="section-label">HR Management</span></div>
              {renderMenuSection({
                title: "HR Module", menuKey: "hr", icon: FaUsers, paths: ["/hr"],
                children: (
                  <>
                    {can.hr.includes("employee") && renderMenuItem("/hr/employees", FaUsers, "Employees")}
                    {can.hr.includes("attendance") && (
                      <>
                        {renderMenuItem("/hr/attendance", FaClock, "Attendance Punch")}
                        {renderMenuItem("/hr/attendance/summary", FaChartLine, "Attendance Summary")}
                      </>
                    )}
                    {can.hr.includes("leave") && (
                      <>
                        {renderMenuItem("/hr/leave/apply", FaCalendarAlt, "Apply Leave")}
                        {role <= 2 && renderMenuItem("/hr/leave/approval", FaCalendarAlt, "Leave Approval")}
                      </>
                    )}
                    {can.hr.includes("payroll") && (
                      <>
                        {renderMenuItem("/hr/payroll", FaMoneyBillWave, "Payroll")}
                        {renderMenuItem("/hr/payslips", FaFileAlt, "Payslips")}
                      </>
                    )}
                    {can.hr.includes("assets") && renderMenuItem("/hr/assets", FaBoxOpen, "Assets")}
                    {can.hr.includes("expenses") && renderMenuItem("/hr/expenses", FaReceipt, "Expenses")}
                    {can.hr.includes("roster") && renderMenuItem("/hr/roster", FaCalendarAlt, "Roster")}
                    {can.hr.includes("documents") && renderMenuItem("/hr/documents", FaFileAlt, "Documents")}
                    {can.hr.includes("letters") && renderMenuItem("/hr/letters", FaFileInvoice, "Letters")}
                    {can.hr.includes("recruitment") && renderMenuItem("/hr/recruitment", FaUserPlus, "Recruitment")}
                    {can.hr.includes("performance") && renderMenuItem("/hr/performance", FaChartLine, "Performance")}
                    {can.hr.includes("ai_chat") && renderMenuItem("/hr/ai-chat", FaRobot, "AI Assistant")}
                    {renderMenuItem("/hr/my-requests", FaInfoCircle, "My Requests")}
                    {can.hr.includes("requisition") && (
                      <>
                        {renderMenuItem("/hr/requisition", FaFileAlt, "Requisition")}
                        {role <= 2 && renderMenuItem("/hr/requisition-approval", FaCheckCircle, "Requisition Approval")}
                      </>
                    )}
                    {can.hr.includes("salary_increment") && renderMenuItem("/hr/salary-increment", FaMoneyBillWave, "Salary Increment")}
                    {can.hr.includes("employee") && renderMenuItem("/staff-deployments", FaBriefcase, "Staff Deployments")}
                    {can.hr.includes("attendance") && renderMenuItem("/event-management", FaCalendarAlt, "Event Management")}
                    {renderMenuItem("/hr/projects", FaFolderOpen, "Projects")}
                    {renderMenuItem("/hr/my-tasks", FaCheckCircle, "My Tasks")}
                  </>
                ),
              })}
            </>
          )}

          {/* ===== SYSTEM ===== */}
          {(can.users?.length > 0 || can.category?.length > 0 || can.items || can.products || can.inventory || can.billing) && (
            <div className="section-divider"><span className="section-label">System</span></div>
          )}

          {can.users?.length > 0 &&
            renderMenuSection({
              title: "Users", menuKey: "users", icon: FaUser, paths: ["/user_setting", "/salary_setting"],
              children: (
                <>
                  {can.users.includes("user") && renderMenuItem("/user_setting", FaUser, "User Setting")}
                  {/* {can.users.includes("salary") && renderMenuItem("/salary_setting", FaWallet, "Salary Setting")} */}
                </>
              ),
            })}

          {can.category?.length > 0 &&
            renderMenuSection({
              title: "Category", menuKey: "category", icon: FaColumns, paths: ["/add_category", "/category"],
              children: (
                <>
                  {can.category.includes("add_category") && renderMenuItem("/add_category", FaColumns, "Add Category")}
                  {can.category.includes("view_category") && renderMenuItem("/category", FaList, "Category List")}
                </>
              ),
            })}

          {can.items &&
            renderMenuSection({
              title: "Item Setting", menuKey: "items", icon: FaBars, paths: ["/add_item", "/item_list"],
              children: (
                <>
                  {renderMenuItem("/add_item", FaBars, "Add Item")}
                  {renderMenuItem("/item_list", FaList, "Item List")}
                </>
              ),
            })}

          {can.products &&
            renderMenuSection({
              title: "Product Setting", menuKey: "products", icon: AiOutlineProduct, paths: ["/add_product", "/product_list"],
              children: (
                <>
                  {renderMenuItem("/add_product", AiOutlineProduct, "Add Product")}
                  {renderMenuItem("/product_list", FaList, "Product List")}
                </>
              ),
            })}

          {/* ===== INVENTORY ===== */}
          {can.inventory &&
            renderMenuSection({
              title: "Inventory",
              menuKey: "inventory",
              icon: FaWarehouse,
              paths: ["/warehouses", "/stock_out", "/stock_in", "/stock_list", "/today"],
              children: (
                <>
                  {renderMenuItem("/warehouses", FaWarehouse, "Warehouses")}
                  {renderMenuItem("/stock_in", FaWarehouse, "Manual Stock In / Returns")}
                  {renderMenuItem("/stock_out", FaWarehouse, "Stock Out")}
                  {renderMenuItem("/stock_list", FaList, "Stock List")}
                  {renderMenuItem("/today", FaClock, "Today's Activity")}
                  {renderMenuItem("/stock-transfers", FaWarehouse, "Stock Transfers")}
                  {renderMenuItem("/product-batches", FaBarcode, "Batch / Serial Tracking")}
                  {renderMenuItem("/bom-list", FaCogs, "Bill of Materials (BOM)")}
                  {renderMenuItem("/work-orders", FaIndustry, "Work Orders (MRP)")}
                  {renderMenuItem("/damage-loss", FaExclamationTriangle, "Damage & Loss")}
                </>
              ),
            })}

          {/* ===== PURCHASE & VENDOR ===== */}
          {(can.inventory || can.billing) &&
            renderMenuSection({
              title: "Purchase & Vendor",
              menuKey: "purchase",
              icon: FaUserFriends,
              paths: ["/vendors", "/purchase-orders", "/purchase_list"],
              children: (
                <>
                  {renderMenuItem("/vendors", FaUserFriends, "Vendors")}
                  {renderMenuItem("/purchase-orders", FaFileInvoice, "Purchase Orders (RFQ)")}
                  {renderMenuItem("/purchase_list", FaWarehouse, "Purchase Receipts / List")}
                  {renderMenuItem("/purchase-returns", FaUndo, "Purchase Returns")}
                </>
              ),
            })}

          {/* ===== BILLING ===== */}
          {can.billing &&
            renderMenuSection({
              title: "Billing",
              menuKey: "billing",
              icon: FaCreditCard,
              paths: ["/quotations", "/add_billing", "/billing_list"],
              children: (
                <>
                  {renderMenuItem("/quotations", FaFileInvoice, "Quotations")}
                  {renderMenuItem("/add_billing", FaCreditCard, "Add Bill")}
                  {renderMenuItem("/billing_list", FaList, "Billing List")}
                  {renderMenuItem("/credit-debit-notes", FaFileInvoice, "Credit / Debit Notes")}
                </>
              ),
            })}

          {/* ===== ACCOUNTING ===== */}
          {(can.inventory || can.billing) && (
            <>
              <div className="section-divider"><span className="section-label">Accounting</span></div>
              {renderMenuSection({
                title: "Accounting Module",
                menuKey: "accounting",
                icon: FaBook,
                paths: [
                  "/chart-of-accounts",
                  "/journal-entries",
                  "/trial-balance",
                  "/profit-loss",
                  "/balance-sheet",
                  "/bank-reconciliation",
                  "/ar-aging",
                  "/ap-aging",
                  "/vat-report",
                ],
                children: (
                  <>
                    {renderMenuItem("/chart-of-accounts", FaList, "Chart of Accounts")}
                    {renderMenuItem("/journal-entries", FaBook, "Journal Entries")}
                    {renderMenuItem("/trial-balance", FaBalanceScale, "Trial Balance")}
                    {renderMenuItem("/profit-loss", FaFileInvoice, "Profit & Loss")}
                    {renderMenuItem("/balance-sheet", FaBalanceScale, "Balance Sheet")}
                    {renderMenuItem("/bank-reconciliation", FaUniversity, "Bank Reconciliation")}
                    {renderMenuItem("/ar-aging", FaHourglassHalf, "AR Aging")}
                    {renderMenuItem("/ap-aging", FaHourglassHalf, "AP Aging")}
                    {renderMenuItem("/vat-report", FaFileInvoice, "VAT Report")}
                  </>
                ),
              })}
            </>
          )}

          {can.reports && (
            <Link to="/reports" title="Reports"
              className={`sidebar-link main ${isActive("/reports") ? "active" : ""}`}
              onClick={closeMobile}>
              <FaChartArea className="link-icon" /><span>Reports</span>
            </Link>
          )}

          {can.customers && (
            <Link to="/customers" title="Customers"
              className={`sidebar-link main ${isActive("/customers") ? "active" : ""}`}
              onClick={closeMobile}>
              <FaUserFriends className="link-icon" /><span>Customers</span>
            </Link>
          )}

          {renderMenuItem("/crm/dashboard", FaChartLine, "CRM Dashboard")}
          {renderMenuItem("/crm/leads", FaUsers, "Leads")}
          {renderMenuItem("/crm/deals", FaHandshake, "Deals Pipeline")}
          {renderMenuItem("/crm/activities", FaCalendarAlt, "Activities")}

          {/* ===== ADMIN ===== */}
          {can.companySetting && (
            <>
              <div className="section-divider"><span className="section-label">Administration</span></div>
              <Link to="/company_setting" title="Settings"
                className={`sidebar-link main ${isActive("/company_setting") ? "active" : ""}`}
                onClick={closeMobile}>
                <FaCog className="link-icon" /><span>Settings</span>
              </Link>
              <Link to="/activity_log" title="Activity Log"
                className={`sidebar-link main ${isActive("/activity_log") ? "active" : ""}`}
                onClick={closeMobile}>
                <FaList className="link-icon" /><span>Activity Log</span>
              </Link>

              {renderMenuItem("/exchange-rates", FaGlobe, "Exchange Rates")}
            </>
          )}
        </div>
      </div>

      {/* ===== STICKY FOOTER ===== */}
      {role === 1 && (
        <div className="sidebar-footer">
          <button
            type="button"
            title="Help & Support"
            className="sidebar-link main help-btn"
            onClick={() => { closeMobile(); setShowSupport(true); }}
          >
            <FaHeadset className="link-icon" />
            <span>Help & Support</span>
            <span className="help-badge">Live</span>
          </button>
        </div>
      )}

      {/* ===== Support Widget ===== */}
      {showSupport && (
        <div className="support-widget-wrapper" ref={supportWidgetRef}>
          <SupportWidget apiUrl={apiUrl} onClose={() => setShowSupport(false)} />
        </div>
      )}
    </div>
  );
};

export default Sidebar;